/**
 * A área de projetos do admin, portada de app.js:769-831.
 *
 * Duas coisas que vêm de lá e ficam: registrar projeto não pede YAML nenhum, e a
 * resposta do registro traz o passo a passo do gatilho no host — sem isso o
 * projeto fica conectado no banco e nunca recebe push. O que muda é a fila de
 * jobs, que era um `setTimeout(renderAdmin, 2500)` redesenhando o painel inteiro
 * e agora é a repetição da consulta (consultas.ts:172-183).
 */
import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postar } from '../api/cliente';
import { chaves, useJobs, useProjetosAdmin } from '../api/consultas';
import type { Job, ProjetoAdmin } from '../api/tipos';
import { Carregando, Falha, Vazio } from '../componentes/Estado';
import { useRecado } from '../componentes/Recado';

interface Opcao { v: string; rotulo: string }
const HOSTS: Opcao[] = [{ v: 'azure', rotulo: 'Azure DevOps' }, { v: 'gitlab', rotulo: 'GitLab' }];
const GATILHOS: Opcao[] = [{ v: 'webhook', rotulo: 'Webhook nativo' }, { v: 'ci', rotulo: 'Stage no CI' }];
const BOOTSTRAPS: Opcao[] = [{ v: 'yes', rotulo: 'Sim, doc do zero' }, { v: 'no', rotulo: 'Só daqui pra frente' }];

interface CorpoDoRegistro {
  slug: string;
  displayName: string;
  gitHost: string;
  orgUrl: string;
  repoPath: string;
  branch: string;
  repoDir: string;
  triggerMode: string;
  bootstrap: boolean;
}

interface Registro {
  ingestToken?: string | null;
  provisionHint?: { steps: string[] } | null;
  job?: { id: string } | null;
}

interface Enfileirado {
  via?: string | null;
  files?: number | null;
  job?: { id: string } | null;
}

const mensagemDe = (e: unknown) => (e instanceof Error ? e.message : String(e || 'erro desconhecido'));

const COR_DO_STATUS: Record<string, string> = { done: '--ok', running: '--warn', failed: '--bad' };

// A tela é do admin, não de um projeto, e por isso ignora o slug da URL — a
// assinatura segue a das outras telas só para caber no mapa de Tela.tsx.
export function Projetos({ slug: _slug }: { slug: string }) {
  const projetos = useProjetosAdmin();

  if (projetos.isPending) return <Carregando rotulo="a área de projetos" />;
  if (projetos.error) {
    return <Falha erro={projetos.error} rotulo="a área de projetos" aoRepetir={projetos.refetch} />;
  }

  return (
    <>
      <div className="crumb">admin<span className="sep">/</span>projetos</div>
      <Formulario />

      <div className="sec-title" style={{ marginTop: 34 }}>Projetos registrados</div>
      {(projetos.data || []).map((p) => <LinhaDoProjeto key={p.slug} projeto={p} />)}

      <div className="sec-title" style={{ marginTop: 30 }}>Jobs recentes</div>
      <FilaDeJobs />
    </>
  );
}

function Formulario() {
  const avisar = useRecado();
  const fila = useQueryClient();
  const [nome, setNome] = useState('');
  const [slugNovo, setSlugNovo] = useState('');
  const [org, setOrg] = useState('');
  const [repo, setRepo] = useState('');
  const [branch, setBranch] = useState('main');
  const [dir, setDir] = useState('');
  const [host, setHost] = useState('azure');
  const [gatilho, setGatilho] = useState('webhook');
  const [bootstrap, setBootstrap] = useState('yes');

  const registro = useMutation({
    mutationFn: (corpo: CorpoDoRegistro) => postar<Registro>('/api/register', corpo),
    onSuccess: (_r, corpo) => {
      avisar(`projeto ${corpo.slug} registrado`);
      // o seletor de projeto do topo e esta lista saem do mesmo cache; sem
      // invalidar, o projeto novo só apareceria num F5 (app.js:830)
      void fila.invalidateQueries({ queryKey: chaves.projetos });
    },
    onError: (e) => avisar(`não deu para registrar: ${mensagemDe(e)}`),
  });

  const enviar = () => {
    const corpo: CorpoDoRegistro = {
      slug: slugNovo.trim() || nome.trim().toLowerCase().replace(/\s+/g, '-'),
      displayName: nome.trim(),
      gitHost: host,
      orgUrl: org.trim(),
      repoPath: repo.trim(),
      branch: branch.trim() || 'main',
      repoDir: dir.trim(),
      triggerMode: gatilho,
      bootstrap: bootstrap === 'yes',
    };
    if (!corpo.slug) { avisar('informe nome/slug'); return; }
    registro.mutate(corpo);
  };

  return (
    <>
      <div className="sec-title">Registrar um projeto</div>
      <div className="sec-sub">
        Conecte um repositório sem editar YAML. A plataforma indica como criar o gatilho no host.
      </div>
      <div className="admin-grid">
        <Campo id="a-name" rotulo="Nome do projeto" placeholder="billing-service" valor={nome} ao={setNome} />
        <Campo id="a-slug" rotulo="Slug" mono placeholder="billing" valor={slugNovo} ao={setSlugNovo} />
        <div className="fld">
          <label>Git host</label>
          <Seg rotulo="Git host" opcoes={HOSTS} valor={host} ao={setHost} />
        </div>
        <Campo id="a-org" rotulo="Organização / URL" mono placeholder="dev.azure.com/empresa" valor={org} ao={setOrg} />
        <Campo id="a-repo" rotulo="Projeto / Repositório" mono placeholder="core/billing-service" valor={repo} ao={setRepo} />
        <Campo id="a-branch" rotulo="Branch" mono valor={branch} ao={setBranch} />
        <Campo
          id="a-dir"
          rotulo="Caminho do repo no disco (PoC local)"
          mono
          largo
          placeholder="D:/repos/billing"
          valor={dir}
          ao={setDir}
        />
        <div className="fld">
          <label>Modo de gatilho</label>
          <Seg rotulo="Modo de gatilho" opcoes={GATILHOS} valor={gatilho} ao={setGatilho} />
        </div>
        <div className="fld">
          <label>Bootstrap ao conectar</label>
          <Seg rotulo="Bootstrap ao conectar" opcoes={BOOTSTRAPS} valor={bootstrap} ao={setBootstrap} />
        </div>
      </div>
      <div style={{ marginTop: 18 }}>
        <button type="button" className="btn" disabled={registro.isPending} onClick={enviar}>
          {registro.isPending ? 'Conectando…' : 'Conectar projeto'}
        </button>
      </div>

      {registro.isError && (
        <div className="banner-bad" style={{ marginTop: 16 }}>
          <div>
            <b>O projeto não foi registrado.</b>
            <div className="muted" style={{ marginTop: 4 }}>{mensagemDe(registro.error)}</div>
          </div>
        </div>
      )}
      {registro.isSuccess && <Dica registro={registro.data} />}
    </>
  );
}

function Dica({ registro }: { registro: Registro }) {
  return (
    <div className="hint-box">
      <h4>Registrado · token de ingest: <code>{registro.ingestToken}</code></h4>
      {registro.provisionHint && (
        <ol>
          {registro.provisionHint.steps.map((s, i) => <li key={i}>{comCodigo(s)}</li>)}
        </ol>
      )}
      {registro.job && (
        <div className="muted" style={{ marginTop: 8 }}>
          Bootstrap enfileirado: <code>{registro.job.id}</code>
        </div>
      )}
    </div>
  );
}

/**
 * Trecho entre crases e URL crua viram `<code>`, como em app.js:829.
 *
 * A quebra é feita numa passada só, e a crase ganha da URL: assim uma URL dentro
 * de crase sai como um `<code>` e não como dois, um dentro do outro.
 */
function comCodigo(passo: string): ReactNode[] {
  const marca = /`([^`]+)`|(https?:\/\/\S+)/g;
  const partes: ReactNode[] = [];
  let lido = 0;
  for (let m = marca.exec(passo); m; m = marca.exec(passo)) {
    if (m.index > lido) partes.push(passo.slice(lido, m.index));
    partes.push(<code key={m.index}>{m[1] ?? m[2]}</code>);
    lido = m.index + m[0].length;
  }
  if (lido < passo.length) partes.push(passo.slice(lido));
  return partes;
}

function LinhaDoProjeto({ projeto }: { projeto: ProjetoAdmin }) {
  const avisar = useRecado();
  const fila = useQueryClient();

  const reprocesso = useMutation({
    mutationFn: () => postar<Enfileirado>(`/api/${projeto.slug}/reprocess`),
    onSuccess: (r) => {
      avisar(`enfileirado ${r.job?.id || ''} · ${r.via || ''} · ${r.files || ''} arq.`);
      void fila.invalidateQueries({ queryKey: chaves.jobs });
    },
    onError: (e) => avisar(mensagemDe(e)),
  });

  return (
    <div className="row-card">
      <span className="pdot" />
      <div style={{ flex: 1 }}>
        <div className="pname">{projeto.slug}</div>
        <div className="pmeta">
          {projeto.git_host || 'local'} · {projeto.docs} docs · {projeto.nodes} nós
        </div>
      </div>
      <button
        type="button"
        className="btn ghost sm"
        disabled={reprocesso.isPending}
        onClick={() => reprocesso.mutate()}
      >
        {reprocesso.isPending ? '…' : 'Reprocessar'}
      </button>
    </div>
  );
}

function FilaDeJobs() {
  const jobs = useJobs();
  const lista = jobs.data || [];

  if (jobs.isPending) return <Carregando rotulo="a fila de jobs" />;
  if (lista.length) return <>{lista.slice(0, 8).map((j) => <LinhaDoJob key={j.id} job={j} />)}</>;
  if (jobs.error) {
    return (
      <div className="banner-bad">
        <div>
          <b>Não deu para ler a fila de jobs.</b>
          <div className="muted" style={{ marginTop: 4 }}>{mensagemDe(jobs.error)}</div>
        </div>
      </div>
    );
  }
  return (
    <Vazio titulo="Nenhum job na fila">
      Jobs aparecem aqui quando um push chega pelo webhook, quando alguém clica em{' '}
      <b>Reprocessar</b> ou quando o CLI roda <code>eng reprocess</code>.
    </Vazio>
  );
}

function LinhaDoJob({ job }: { job: Job }) {
  const ultima = job.log?.length ? job.log[job.log.length - 1] || '' : '';
  return (
    <div className="row-card">
      <span className="pdot" style={{ background: `var(${COR_DO_STATUS[job.status] || '--ink-faint'})` }} />
      <div style={{ flex: 1 }}>
        <div className="pname">{job.id} · {job.slug} · {job.mode}</div>
        <div className="pmeta">
          {job.status}{job.error ? ` · ${job.error}` : ''}{ultima ? ` · ${ultima}` : ''}
        </div>
      </div>
    </div>
  );
}

function Campo({ id, rotulo, valor, ao, placeholder, mono, largo }: {
  id: string;
  rotulo: string;
  valor: string;
  ao: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
  largo?: boolean;
}) {
  return (
    <div className={`fld${largo ? ' wide' : ''}`}>
      <label htmlFor={id}>{rotulo}</label>
      <input
        id={id}
        className={mono ? 'mono' : undefined}
        placeholder={placeholder}
        value={valor}
        onChange={(e) => ao(e.target.value)}
      />
    </div>
  );
}

function Seg({ rotulo, opcoes, valor, ao }: {
  rotulo: string;
  opcoes: Opcao[];
  valor: string;
  ao: (v: string) => void;
}) {
  return (
    <div className="seg" role="group" aria-label={rotulo}>
      {opcoes.map((o) => (
        <button
          key={o.v}
          type="button"
          className={valor === o.v ? 'on' : undefined}
          aria-pressed={valor === o.v}
          onClick={() => ao(o.v)}
        >
          {o.rotulo}
        </button>
      ))}
    </div>
  );
}
